import { Suspense } from 'react';
import { Outlet } from 'react-router';
import { Box, Container, GlobalStyles, Stack } from '@mui/material';

import { LoaderContainer } from '../atoms/LoaderContainer';
import JackpotList from '../molecules/JackpotList';
import DrawerNavigation from '../molecules/sidebar/DrawerNavigation';
import MobileBottomNavShell from './MobileBottomNavShell';
import CMSSideContent from '../organisms/CMSSideContent';
import JackpotWinnerOverlay from '../organisms/jackpot/JackpotWinnerOverlay';
import { SkinnedBetslipDrawer, SkinnedReofferedBetslipDrawer } from '../../config/skinRegistry/components';
import { SkinnedHeader } from '../../config/skinRegistry';

import { InstallPromptProvider } from '../../contexts/InstallPromptContext';
import { useVerifyEmailRedirect } from '../../hooks/useVerifyEmailRedirect';
import { usePromoCodeTracking } from '../../hooks/usePromoCodeTracking';
import { useSportsbookLayoutShell } from '../../hooks/components';
import useProductAvailability from '../../hooks/useProductAvailability';
import { PRODUCT } from '../../types/enums';
import { mergeSx } from '../../utils/styles';

import { dynamicViewportHeight } from '../../styles/mixins';

const styles = {
  container: (isFullWidth: boolean) => ({
    display: 'flex',
    width: '100%',
    height: '100vh',
    maxWidth: isFullWidth ? '100vw !important' : '2400px !important',
    mx: isFullWidth ? 0 : 'auto',
  }),
  main: (paddingTop: string, paddingBottom: string | number) => ({
    width: 1,
    flexGrow: 1,
    minWidth: 0,
    overflowX: 'hidden',
    ...dynamicViewportHeight(),
    WebkitOverflowScrolling: 'touch',
    '--header-height': paddingTop,
    pt: paddingTop,
    pb: paddingBottom,
    px: 0,
  }),
  sideContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 270px',
    pt: 8,
    overflow: 'auto',
  },
  desktopJackpotList: {
    maxHeight: 255,
    overflowX: { xs: 'scroll', lg: 'hidden' },
  },
  cmsContent: {
    flex: 1,
  },
};

const SportsbookLayout = () => {
  usePromoCodeTracking();
  useVerifyEmailRedirect();

  const { hasSportsbookAccess } = useProductAvailability();
  const {
    canCompactSidebar,
    isDrawerOpen,
    isFullWidth,
    isSidebarCompact,
    mainPaddingBottom,
    mainPaddingTop,
    mobileBottomNavInset,
    showJackpotList,
    showSideContent,
    toggleDrawerVisibility,
    toggleSidebarCompact,
  } = useSportsbookLayoutShell();

  return (
    <InstallPromptProvider>
      <Container disableGutters sx={styles.container(isFullWidth)}>
        <GlobalStyles
          styles={{
            ':root': {
              '--header-height': mainPaddingTop,
              '--mobile-bottom-nav-height': mobileBottomNavInset,
            },
          }}
        />
        <SkinnedHeader
          isDrawerOpen={isDrawerOpen}
          isSidebarCompact={isSidebarCompact}
          showSidebarCollapse={canCompactSidebar}
          toggleDrawerVisibility={toggleDrawerVisibility}
          toggleSidebarCompact={toggleSidebarCompact}
        />

        <DrawerNavigation
          isOpen={isDrawerOpen}
          isCompact={isSidebarCompact}
          toggleVisibility={toggleDrawerVisibility}
        />
        <MobileBottomNavShell isDrawerOpen={isDrawerOpen} toggleDrawerVisibility={toggleDrawerVisibility} />

        <JackpotWinnerOverlay product={PRODUCT.SPORTSBOOK} />
        <JackpotWinnerOverlay product={PRODUCT.CASINO} />

        <Stack component='main' sx={styles.main(mainPaddingTop, mainPaddingBottom)}>
          <Suspense fallback={<LoaderContainer />}>
            <Outlet />
          </Suspense>
        </Stack>

        {showSideContent && (
          <Box sx={styles.sideContentContainer}>
            {showJackpotList && <JackpotList sx={mergeSx(styles.desktopJackpotList, { overflow: 'auto' })} />}
            <CMSSideContent sx={styles.cmsContent} />
          </Box>
        )}
        {hasSportsbookAccess && (
          <>
            <SkinnedBetslipDrawer />
            <SkinnedReofferedBetslipDrawer />
          </>
        )}
      </Container>
    </InstallPromptProvider>
  );
};

export default SportsbookLayout;
